import { FC, useCallback, useEffect, useState } from 'react';
import {
  Button,
  DataTable,
  InlineNotification,
  Link,
  Loading,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Tile,
} from '@carbon/react';
import { Add, Edit, Flag, Movement, TrashCan } from '@carbon/react/icons';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { DocumentResponse } from 'types/api-responses';
import request from 'lib/request';
import {
  CLIENT_SESSION_ID,
  fetchStructuredPartiesListAtom,
  partyUrl,
  removePartyAtom,
  selectedPartyIndexAtom,
  structuredPartiesListDataAtom,
  structuredPartiesListLoadingAtom,
} from './atoms/structured-parties';
import {
  auditLogVersionAtom,
  clearAddressFlagUrl,
} from './atoms/structured-addresses';
import { AddPartyPanel } from './add-party-panel';
import { ClearFlagsModal, type ClearFlagAddress } from './clear-flags-modal';
import { StagedEntityEditorPanel } from './staged-entity-editor-panel';
import { MoveAddressModal } from './move-address-modal';
import { useMoveAddress, useReadPartyDetail } from './hooks';
import {
  correctStatusLockReason,
  moveAddressBlockedReason,
  partyRowLock,
  STATUS_CELL_CONFIG,
} from './party-status';
import { AddressReviewFlag } from './address-review-flag';
import { IconAction } from './blocked-action';
import type {
  EntityCard,
  StructuredPartyDetail,
  StructuredPartyListItem,
} from './types';
import { anchorCard, anchorName, cardAddresses } from './utils';
import { FormattedCount } from 'components/formatted-count';
import { entityIdSuffix } from 'utils/entity-id';
import { actionErrorMessage } from 'utils/api-errors';
import style from './parties-overview.module.scss';

interface Props {
  document: DocumentResponse;
  addPartyOpen?: boolean;
  onAddPartyOpenChange?: (open: boolean) => void;
}

const HEADERS = [
  { key: 'partyNo', header: 'No.' },
  { key: 'status', header: 'Status' },
  { key: 'details', header: 'Party Details from Document' },
  { key: 'actions', header: 'Actions' },
  { key: 'match', header: 'Confirmed Entity Match' },
];

// Optional cap on addresses rendered inline. When `cap` is set, anything
// beyond it collapses to a "View N more addresses" link pointing at the
// entity detail page (matching prior master behavior). The staged-entity
// cell omits the cap entirely — see callsite for rationale.
const MATCH_ADDRESS_VISIBLE_CAP = 2;

interface RenderAddressListOptions {
  emptyText?: string;
  cap?: number;
}

const renderAddressList = (
  card: EntityCard | null | undefined,
  options: RenderAddressListOptions = {}
) => {
  const { emptyText = '—', cap } = options;
  const addresses = cardAddresses(card);
  if (addresses.length === 0) {
    return <span>{emptyText}</span>;
  }
  const visible = cap !== undefined ? addresses.slice(0, cap) : addresses;
  const overflow = addresses.length - visible.length;
  return (
    <span className={style.addressList}>
      {visible.map((addr, i) => {
        const highlightClass = addr.reviewFlag
          ? `${style.confidenceHighlight} ${
              addr.reviewFlag.criticality === 'high'
                ? style.confidenceHighlightHigh
                : style.confidenceHighlightMed
            }`
          : '';
        const text = addr.fullText || addr.asFoundAddress || '—';
        return (
          <span key={addr.id} className={style.addressLine}>
            <span className={style.addressText}>
              {addresses.length > 1 && (
                <span className={style.addressLabel}>
                  {addr.primary ? 'Primary:' : `${i + 1}.`}{' '}
                </span>
              )}
              <span className={highlightClass}>{text}</span>
            </span>
            {addr.reviewFlag && <AddressReviewFlag flag={addr.reviewFlag} />}
          </span>
        );
      })}
      {overflow > 0 && card?.id && (
        <span className={style.addressLine}>
          <Link
            href={`/entities/${card.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            {`View ${overflow} more address${overflow === 1 ? '' : 'es'}`}
          </Link>
        </span>
      )}
    </span>
  );
};

const entityTypeLabel = (type?: EntityCard['type']) => {
  switch (type) {
    case 'IndividualEntity':
      return 'Individual';
    case 'OrganizationEntity':
      return 'Organization';
    case 'ImportedEntity':
      return 'Imported';
    default:
      return '—';
  }
};

export const PartiesOverview: FC<Props> = ({
  document,
  addPartyOpen,
  onAddPartyOpenChange,
}) => {
  const readPartyDetail = useReadPartyDetail();
  const data = useAtomValue(structuredPartiesListDataAtom);
  const loading = useAtomValue(structuredPartiesListLoadingAtom);
  const load = useSetAtom(fetchStructuredPartiesListAtom);
  const href = document.resources.structuredParties?.href;
  const setSelectedIndex = useSetAtom(selectedPartyIndexAtom);
  const [internalAddOpen, setInternalAddOpen] = useState(false);
  const addOpen = addPartyOpen !== undefined ? addPartyOpen : internalAddOpen;
  const setAddOpen = (open: boolean) => {
    if (onAddPartyOpenChange) onAddPartyOpenChange(open);
    else setInternalAddOpen(open);
  };

  const [searchValue, setSearchValue] = useState('');

  const [deleteTarget, setDeleteTarget] =
    useState<StructuredPartyListItem | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [clearFlagsTarget, setClearFlagsTarget] =
    useState<StructuredPartyListItem | null>(null);
  const [clearFlagsAddresses, setClearFlagsAddresses] = useState<
    ClearFlagAddress[]
  >([]);
  const [clearingFlags, setClearingFlags] = useState(false);
  const [clearFlagsError, setClearFlagsError] = useState<string | null>(null);

  const [editTargetDetail, setEditTargetDetail] =
    useState<StructuredPartyDetail | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const setAuditLogVersion = useSetAtom(auditLogVersionAtom);
  const refresh = useCallback(() => {
    if (href) load(href);
    setAuditLogVersion((v) => v + 1);
  }, [href, load, setAuditLogVersion]);

  const [removePartyState, triggerRemoveParty] = useAtom(removePartyAtom);
  const deleting = removePartyState.loading;

  const handleDelete = useCallback((party: StructuredPartyListItem) => {
    setActionError(null);
    setDeleteTarget(party);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!deleteTarget) return;
    setActionError(null);
    triggerRemoveParty({
      url: partyUrl(document.id, deleteTarget.id, deleteTarget.kind),
      type: 'DELETE',
      data: { client_session_id: CLIENT_SESSION_ID },
      onSuccess: () => {
        setDeleteTarget(null);
        refresh();
      },
      onError: (err) =>
        setActionError(actionErrorMessage(err, 'Could not remove party.')),
    });
  }, [deleteTarget, document.id, refresh, triggerRemoveParty]);

  const openClearFlags = useCallback((party: StructuredPartyListItem) => {
    const addresses = cardAddresses(anchorCard(party))
      .filter((a) => a.reviewFlag && a.id !== null && a.id !== undefined)
      .map((a) => ({
        id: a.id as number,
        label: a.fullText || a.asFoundAddress || `Address #${a.id}`,
      }));
    setClearFlagsAddresses(addresses);
    setClearFlagsTarget(party);
    setClearFlagsError(null);
  }, []);

  const confirmClearFlags = useCallback(
    async (ids: number[]) => {
      if (!clearFlagsTarget || ids.length === 0) return;
      setClearingFlags(true);
      setClearFlagsError(null);
      try {
        await Promise.all(
          ids.map((addressId) => {
            const controller = new AbortController();
            return request.post<unknown, { error?: string }>(
              clearAddressFlagUrl(document.id, addressId),
              {},
              controller
            );
          })
        );
        setClearFlagsTarget(null);
        refresh();
        // If the editor panel is open for the same party, refresh its detail so
        // the flag icon disappears without requiring the user to close and reopen.
        if (
          editOpen &&
          editTargetDetail?.id === clearFlagsTarget.id &&
          editTargetDetail?.kind === clearFlagsTarget.kind
        ) {
          const updated = await readPartyDetail(clearFlagsTarget.href).catch(
            () => null
          );
          if (updated) setEditTargetDetail(updated);
        }
      } catch (e) {
        setClearFlagsError(
          e instanceof Error ? e.message : 'Could not clear flag(s).'
        );
      } finally {
        setClearingFlags(false);
      }
    },
    [
      clearFlagsTarget,
      document.id,
      refresh,
      editOpen,
      editTargetDetail,
      readPartyDetail,
    ]
  );

  const moveAddress = useMoveAddress({
    document,
    allParties: data?.parties ?? [],
    onMoved: () => setAuditLogVersion((v) => v + 1),
  });
  /**
   * Opens Move Address for a party's row.
   *
   * @param {StructuredPartyListItem} party - The party to move from.
   */
  const openMove = (party: StructuredPartyListItem) => {
    setActionError(null);
    moveAddress.open(party);
  };

  const openEdit = useCallback(
    async (party: StructuredPartyListItem) => {
      setActionError(null);
      setEditLoading(true);
      try {
        const detail = await readPartyDetail(party.href).catch(() => null);
        if (!detail) {
          setActionError('Could not load party details.');
          return;
        }
        setEditTargetDetail(detail);
        setEditOpen(true);
      } finally {
        setEditLoading(false);
      }
    },
    [readPartyDetail]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!href) {
    return (
      <div className={style.empty}>
        Structured parties are not available for this document.
      </div>
    );
  }

  if (loading && !data) return <Loading withOverlay={false} />;

  if (!data) {
    return (
      <div className={style.empty}>
        Unable to load parties for this document.
      </div>
    );
  }

  const { parties, summary } = data;

  const partySearchText = (p: StructuredPartyListItem): string => {
    const anchor = anchorCard(p);
    const match = p.confirmedEntity;
    const fmt = (card: EntityCard | null | undefined) =>
      cardAddresses(card)
        .map((a) => a.fullText || a.asFoundAddress || '')
        .join(' ');
    return [
      p.name,
      p.role,
      fmt(anchor),
      match?.name,
      match?.id ? String(match.id) : '',
      fmt(match),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  };

  const search = searchValue.trim().toLowerCase();
  const filteredParties = search
    ? parties.filter((p) => partySearchText(p).includes(search))
    : parties;

  const rows = filteredParties.map((p) => ({
    id: `${p.kind}-${p.id}`,
  }));

  const hasFlaggedParty = parties.some(
    (p) =>
      p.status !== 'removed' &&
      cardAddresses(anchorCard(p)).some((a) => a.reviewFlag)
  );

  const body = (
    <div className={style.body}>
      {hasFlaggedParty && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="Address confidence issues"
          subtitle="Some party addresses were flagged for low confidence. See review flags below. These parties may require your review."
        />
      )}

      <div className={style.summaryTiles}>
        <Tile>
          <p className={style.tileCount}>
            <FormattedCount value={summary.counts.notStarted ?? 0} />
          </p>
          <p className={style.tileLabel}>Not Started</p>
        </Tile>
        <Tile>
          <p className={style.tileCount}>
            <FormattedCount value={summary.counts.complete ?? 0} />
          </p>
          <p className={style.tileLabel}>Complete</p>
        </Tile>
        <Tile>
          <p className={style.tileCount}>
            <FormattedCount value={summary.counts.removed ?? 0} />
          </p>
          <p className={style.tileLabel}>Removed</p>
        </Tile>
        <Tile>
          <p className={style.tileCount}>
            <FormattedCount value={summary.counts.userEdited ?? 0} />
          </p>
          <p className={style.tileLabel}>User Edited</p>
        </Tile>
        <Tile>
          <p className={style.tileCount}>
            <FormattedCount value={summary.counts.userAdded ?? 0} />
          </p>
          <p className={style.tileLabel}>User Added</p>
        </Tile>
      </div>

      <div className={style.overviewTable}>
        <DataTable rows={rows} headers={HEADERS}>
          {({ headers, getHeaderProps }) => (
            <TableContainer>
              <TableToolbar size="sm">
                <TableToolbarContent>
                  <TableToolbarSearch
                    placeholder="Search parties…"
                    persistent
                    size="sm"
                    value={searchValue}
                    onChange={(e) =>
                      setSearchValue(
                        (e as React.ChangeEvent<HTMLInputElement>).target
                          ?.value ?? ''
                      )
                    }
                  />
                  <Button
                    kind="primary"
                    size="sm"
                    renderIcon={Add}
                    onClick={() => setAddOpen(true)}
                  >
                    Add Party
                  </Button>
                </TableToolbarContent>
              </TableToolbar>
              <Table size="md">
                <TableHead>
                  <TableRow>
                    {headers.map((h) => (
                      <TableHeader
                        {...getHeaderProps({ header: h })}
                        key={h.key}
                        className={
                          h.key === 'match' ? style.matchHeader : undefined
                        }
                      >
                        {h.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredParties.map((party) => {
                    const i = parties.indexOf(party);
                    const isRemoved = party.status === 'removed';
                    const isUserAdded = party.stagedEntity === null;
                    const anchor = anchorCard(party);
                    const match = party.confirmedEntity;
                    const rowClass = [
                      style.row,
                      isRemoved ? style.rowRemoved : '',
                    ]
                      .filter(Boolean)
                      .join(' ');

                    return (
                      <TableRow
                        key={`${party.kind}-${party.id}`}
                        className={rowClass}
                        onClick={
                          isRemoved ? undefined : () => setSelectedIndex(i)
                        }
                      >
                        <TableCell className={style.cellMiddle}>
                          {!isRemoved && (
                            <span className={style.partyNo}>
                              {party.partyNumber || i + 1}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className={style.cellMiddle}>
                          {(() => {
                            const cfg = STATUS_CELL_CONFIG[party.status];
                            const Icon = cfg.icon;
                            return (
                              <span
                                className={`${style.statusCell} ${
                                  style[cfg.className]
                                }`}
                              >
                                <Icon />
                                {cfg.label}
                              </span>
                            );
                          })()}
                        </TableCell>
                        <TableCell className={style.cellTop}>
                          {isUserAdded ? (
                            <>
                              <span className={style.primaryLabel}>
                                User Added
                              </span>
                              <div className={style.cellMeta}>
                                <span>
                                  Role:{' '}
                                  <span className={style.metaValue}>
                                    {party.role || '—'}
                                  </span>
                                </span>
                              </div>
                            </>
                          ) : (
                            <>
                              {anchor?.id ? (
                                <Link
                                  href={`/entities/${anchor.id}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {anchorName(party)}
                                </Link>
                              ) : (
                                <span>{anchorName(party)}</span>
                              )}
                              <div className={style.cellMeta}>
                                <span>
                                  Role:{' '}
                                  <span className={style.metaValue}>
                                    {party.role || '—'}
                                  </span>
                                </span>
                                <span>
                                  Entity Type:{' '}
                                  <span className={style.metaValue}>
                                    {entityTypeLabel(anchor?.type)}
                                  </span>
                                </span>
                                <span className={style.addressRow}>
                                  {cardAddresses(anchor).length > 1
                                    ? 'Addresses:'
                                    : 'Address:'}{' '}
                                  <span className={style.metaValue}>
                                    {renderAddressList(anchor)}
                                  </span>
                                </span>
                              </div>
                            </>
                          )}
                        </TableCell>
                        <TableCell
                          className={`${style.cellMiddle} ${style.actionsCell}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {(() => {
                            if (isRemoved) return null;
                            const { locked: isLocked, removeLocked } =
                              partyRowLock(party);
                            const hasFlag = cardAddresses(anchor).some(
                              (a) => a.reviewFlag
                            );
                            const hasAddresses =
                              cardAddresses(anchor).length > 0;
                            const moveBlocked = moveAddressBlockedReason(party);
                            return (
                              <div className={style.rowActions}>
                                {hasFlag && (
                                  <IconAction
                                    label="Clear Flag"
                                    renderIcon={Flag}
                                    blockedReason={correctStatusLockReason(
                                      isLocked,
                                      'clear flag'
                                    )}
                                    onClick={() => openClearFlags(party)}
                                  />
                                )}
                                <IconAction
                                  label="Edit Entity Details"
                                  renderIcon={Edit}
                                  blockedReason={correctStatusLockReason(
                                    isLocked,
                                    'edit'
                                  )}
                                  disabled={editLoading}
                                  onClick={() => openEdit(party)}
                                />
                                <IconAction
                                  label="Move Address"
                                  renderIcon={Movement}
                                  blockedReason={
                                    correctStatusLockReason(
                                      isLocked,
                                      'move address'
                                    ) ??
                                    moveBlocked ??
                                    (hasAddresses
                                      ? null
                                      : 'No addresses to move')
                                  }
                                  onClick={() => openMove(party)}
                                />
                                <IconAction
                                  label="Remove Party"
                                  renderIcon={TrashCan}
                                  blockedReason={correctStatusLockReason(
                                    removeLocked,
                                    'remove'
                                  )}
                                  onClick={() => handleDelete(party)}
                                />
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell className={style.cellMiddle}>
                          {isRemoved ? null : match ? (
                            <>
                              <Link
                                href={`/entities/${match.id}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {match.name} {entityIdSuffix(match.id)}
                              </Link>
                              <div className={style.cellMeta}>
                                <span>
                                  Entity Type:{' '}
                                  <span className={style.metaValue}>
                                    {entityTypeLabel(match.type)}
                                  </span>
                                </span>
                                <span className={style.addressRow}>
                                  {cardAddresses(match).length > 1
                                    ? 'Addresses:'
                                    : 'Primary Address:'}{' '}
                                  <span className={style.metaValue}>
                                    {renderAddressList(match, {
                                      cap: MATCH_ADDRESS_VISIBLE_CAP,
                                    })}
                                  </span>
                                </span>
                              </div>
                            </>
                          ) : party.pendingSuggestionsCount > 0 ? (
                            <span className={style.noMatch}>
                              {party.pendingSuggestionsCount} suggestion
                              {party.pendingSuggestionsCount === 1
                                ? ''
                                : 's'}{' '}
                              available
                            </span>
                          ) : party.noMatchSuggestion ? (
                            <span className={style.noMatch}>
                              No matches found
                            </span>
                          ) : (
                            <span className={style.noMatch}>
                              No suggestions available yet
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      </div>
    </div>
  );

  return (
    <>
      {body}
      <AddPartyPanel
        open={addOpen}
        document={document}
        onClose={() => setAddOpen(false)}
        onAdded={() => {
          setAddOpen(false);
          refresh();
        }}
      />

      <Modal
        open={!!deleteTarget}
        danger
        modalHeading="Remove Party"
        primaryButtonText={deleting ? 'Removing…' : 'Remove Party'}
        secondaryButtonText="Cancel"
        primaryButtonDisabled={deleting}
        onRequestClose={() => setDeleteTarget(null)}
        onSecondarySubmit={() => setDeleteTarget(null)}
        onRequestSubmit={confirmDelete}
      >
        <p>
          Are you sure you want to remove{' '}
          <strong>{deleteTarget?.name || 'this party'}</strong> from the
          structured provisions for this document?
        </p>
        {actionError && (
          <div className={style.modalNotification}>
            <InlineNotification
              kind="error"
              lowContrast
              title="Error"
              subtitle={actionError}
              hideCloseButton
            />
          </div>
        )}
      </Modal>

      <ClearFlagsModal
        open={!!clearFlagsTarget}
        addresses={clearFlagsAddresses}
        loading={clearingFlags}
        error={clearFlagsError}
        onClose={() => setClearFlagsTarget(null)}
        onConfirm={confirmClearFlags}
      />

      <MoveAddressModal {...moveAddress.modalProps} />

      {editTargetDetail && (
        <StagedEntityEditorPanel
          open={editOpen}
          document={document}
          party={editTargetDetail}
          allParties={data?.parties ?? []}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            refresh();
          }}
        />
      )}
    </>
  );
};
